import React, { useEffect, useRef, useState } from "react";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { fromLonLat } from "ol/proj";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import Style from "ol/style/Style";
import Icon from "ol/style/Icon";
import Vector from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import Geometry from "ol/geom/Geometry";
import Stroke from "ol/style/Stroke";
import Fill from "ol/style/Fill";

interface Props {
  coordinates: { latitude: number; longitude: number };
}

const RiverSensorLocation: React.FC<Props> = ({ coordinates }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<Map | null>(null);
  const [markerSource, setMarkerSource] = useState(new VectorSource());
  const url = require("../data/1country_boundary.geojson");

  const stylesThin: Style[] = [
    new Style({
      stroke: new Stroke({
        color: "#55A4CC",
        width: 1,
      }),
    }),
  ];

  const landFillStyle = new Fill({
    color: [114, 159, 166, 0.8],
  });

  const countryBoundaryStyle = new Style({
    fill: landFillStyle,
    stroke: new Stroke({
      color: "#55A4CC",
      width: 1,
    }),
  });

  useEffect(() => {
    if (mapRef.current && !mapInstance.current) {
      const countryBoundarySource = new Vector({
        url: url,
        format: new GeoJSON(),
        loader: function () {
          const xhr = new XMLHttpRequest();
          xhr.open("GET", url);
          xhr.onload = function () {
            if (xhr.status === 200) {
              const features = countryBoundarySource
                .getFormat()
                ?.readFeatures(xhr.responseText) as Feature<Geometry>[];
              features?.forEach((feature) => {
                feature.getGeometry()?.transform("EPSG:4326", "EPSG:3857");
              });
              countryBoundarySource.addFeatures(features);
            } else {
              console.error("Failed to load GeoJSON file: " + url);
            }
          };
          xhr.send();
        },
      });

      const countryBoundaryLayerThin = new VectorLayer({
        source: countryBoundarySource,
        style: stylesThin,
      });

      const countryBoundaryLayer = new VectorLayer({
        source: countryBoundarySource,
        style: [countryBoundaryStyle],
      });

      const rasterLayer = new TileLayer({
        source: new OSM(),
        visible: true, // Initially visible
      });

      const vectorLayer = new VectorLayer({
        source: new VectorSource({ wrapX: false }),
      });

      const markerLayer = new VectorLayer({
        source: markerSource,
      });

      const view = new View({
        center: fromLonLat([coordinates.longitude, coordinates.latitude]),
        zoom: 8,
      });

      mapInstance.current = new Map({
        layers: [rasterLayer, countryBoundaryLayerThin, countryBoundaryLayer, vectorLayer, markerLayer],
        controls: [],
        target: mapRef.current,
        view: view,
      });

      // Zoom-level based visibility handling
      view.on("change:resolution", () => {
        const zoom = view.getZoom() || 0;
        if (zoom > 9) {
          rasterLayer.setVisible(true);
          countryBoundaryLayerThin.setVisible(false);
          countryBoundaryLayer.setVisible(false);
        } else {
          rasterLayer.setVisible(false);
          countryBoundaryLayerThin.setVisible(true);
          countryBoundaryLayer.setVisible(true);
        }
      });

      // Initial layer visibility check
      const initialZoom = view.getZoom() || 0;
      rasterLayer.setVisible(initialZoom > 9);
      countryBoundaryLayerThin.setVisible(initialZoom <= 9);
      countryBoundaryLayer.setVisible(initialZoom <= 9);
    }

    // Marker feature setup
    const markerFeature = new Feature({
      geometry: new Point(fromLonLat([coordinates.longitude, coordinates.latitude])),
    });

    const markerStyle = new Style({
      image: new Icon({
        src: "https://openlayers.org/en/latest/examples/data/icon.png",
        scale: 0.5,
      }),
    });

    markerFeature.setStyle(markerStyle);
    markerSource.clear();
    markerSource.addFeature(markerFeature);

    return () => {
      if (mapInstance.current) {
        mapInstance.current.setTarget(undefined);
        mapInstance.current = null;
      }
    };
  }, [coordinates, markerSource]);

  return (
    <div style={{ height: "100%", width: "100%" ,backgroundColor:"var(--main-bg)"}}>
      <div style={{ height: "100%" }} ref={mapRef}></div>
    </div>
  );
};

export default RiverSensorLocation;
